import React, { useState, useEffect, useCallback } from "react";
import { api, useBusiness } from "@/App";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";

const formatCurrency = (v) => new Intl.NumberFormat("en-US", { style: "currency", currency: "USD" }).format(v || 0);

const MileageTracker = () => {
  const { selectedBusiness } = useBusiness();
  const [logs, setLogs] = useState([]);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showDialog, setShowDialog] = useState(false);
  const [editingLog, setEditingLog] = useState(null);
  const [form, setForm] = useState({
    trip_date: new Date().toISOString().split("T")[0],
    start_location: "", end_location: "", purpose: "", miles: "", parking_tolls: ""
  });

  const fetchData = useCallback(async () => {
    if (!selectedBusiness) return;
    setLoading(true);
    try {
      const [logsRes, summaryRes] = await Promise.all([
        api.get(`/mileage-logs/${selectedBusiness.id}`),
        api.get(`/mileage-logs/${selectedBusiness.id}/summary`)
      ]);
      setLogs(logsRes.data);
      setSummary(summaryRes.data);
    } catch { toast.error("Failed to load mileage data"); }
    finally { setLoading(false); }
  }, [selectedBusiness]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const resetForm = () => {
    setForm({ trip_date: new Date().toISOString().split("T")[0], start_location: "", end_location: "", purpose: "", miles: "", parking_tolls: "" });
    setEditingLog(null);
  };

  const openAdd = () => { resetForm(); setShowDialog(true); };
  const openEdit = (log) => {
    setEditingLog(log);
    setForm({
      trip_date: log.trip_date, start_location: log.start_location || "",
      end_location: log.end_location || "", purpose: log.purpose,
      miles: log.miles.toString(), parking_tolls: (log.parking_tolls || "").toString()
    });
    setShowDialog(true);
  };

  const saveLog = async () => {
    if (!form.purpose || !form.miles) { toast.error("Purpose and miles are required"); return; }
    try {
      if (editingLog) {
        await api.put(`/mileage-logs/${editingLog.id}`, {
          trip_date: form.trip_date, start_location: form.start_location || null,
          end_location: form.end_location || null, purpose: form.purpose,
          miles: parseFloat(form.miles), parking_tolls: form.parking_tolls ? parseFloat(form.parking_tolls) : null
        });
        toast.success("Trip updated");
      } else {
        await api.post("/mileage-logs", {
          business_id: selectedBusiness.id, trip_date: form.trip_date,
          start_location: form.start_location || null, end_location: form.end_location || null,
          purpose: form.purpose, miles: parseFloat(form.miles),
          parking_tolls: form.parking_tolls ? parseFloat(form.parking_tolls) : null
        });
        toast.success("Trip logged");
      }
      setShowDialog(false); resetForm(); fetchData();
    } catch { toast.error("Failed to save trip"); }
  };

  const deleteLog = async (id) => {
    try { await api.delete(`/mileage-logs/${id}`); toast.success("Trip deleted"); fetchData(); }
    catch { toast.error("Failed to delete"); }
  };

  if (!selectedBusiness) return <div className="flex items-center justify-center min-h-[400px]"><p className="text-muted-foreground">Please select a business.</p></div>;
  if (loading) return <div className="flex items-center justify-center min-h-[400px]"><div className="animate-pulse text-muted-foreground">Loading...</div></div>;

  return (
    <div className="space-y-6" data-testid="mileage-tracker-page">
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold text-stone-900">Mileage Tracker</h1>
          <p className="text-stone-600">Log business trips for IRS mileage deduction</p>
        </div>
        <Button onClick={openAdd} className="bg-[#1e3a5f] hover:bg-[#2d5a8e]" data-testid="add-trip-btn">+ Log Trip</Button>
      </div>

      {summary && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <Card><CardContent className="pt-5 pb-4 text-center">
            <p className="text-2xl font-bold text-stone-800">{summary.total_trips}</p>
            <p className="text-xs text-stone-500 mt-1">Total Trips</p>
          </CardContent></Card>
          <Card><CardContent className="pt-5 pb-4 text-center">
            <p className="text-2xl font-bold text-blue-600">{summary.total_miles.toLocaleString()}</p>
            <p className="text-xs text-stone-500 mt-1">Total Miles</p>
          </CardContent></Card>
          <Card><CardContent className="pt-5 pb-4 text-center">
            <p className="text-2xl font-bold text-stone-600">${summary.mileage_rate}/mi</p>
            <p className="text-xs text-stone-500 mt-1">IRS Rate (2025)</p>
          </CardContent></Card>
          <Card className="border-[#1e3a5f]/20 bg-[#1e3a5f]/5"><CardContent className="pt-5 pb-4 text-center">
            <p className="text-2xl font-bold text-[#1e3a5f]">{formatCurrency(summary.estimated_deduction)}</p>
            <p className="text-xs text-[#1e3a5f] mt-1">Estimated Deduction</p>
          </CardContent></Card>
        </div>
      )}

      <Card>
        <CardHeader><CardTitle className="text-lg">Trip Log</CardTitle></CardHeader>
        <CardContent>
          {logs.length === 0 ? (
            <p className="text-center text-muted-foreground py-8">No trips logged yet. Start by logging your first business trip.</p>
          ) : (
            <div className="space-y-2">
              {logs.map(log => (
                <div key={log.id} className="flex items-center gap-3 p-3 rounded-lg border border-stone-200 hover:border-stone-300 transition-colors" data-testid={`trip-${log.id}`}>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-0.5">
                      <span className="font-medium text-stone-900 text-sm">{log.purpose}</span>
                      <Badge variant="outline" className="text-[10px]">{log.miles} mi</Badge>
                      {log.parking_tolls > 0 && <Badge variant="outline" className="text-[10px] text-amber-600 border-amber-300">+{formatCurrency(log.parking_tolls)} P&T</Badge>}
                    </div>
                    <div className="flex items-center gap-2 text-xs text-stone-500">
                      <span>{log.trip_date}</span>
                      {log.start_location && <><span>·</span><span>{log.start_location}</span></>}
                      {log.end_location && <><span>→</span><span>{log.end_location}</span></>}
                    </div>
                  </div>
                  <span className="font-semibold text-sm text-[#1e3a5f]">{formatCurrency(log.miles * (summary?.mileage_rate || 0.70))}</span>
                  <button onClick={() => openEdit(log)} className="p-1.5 rounded hover:bg-stone-100" data-testid={`edit-trip-${log.id}`}>
                    <svg className="w-4 h-4 text-stone-400" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="1.5"><path strokeLinecap="round" strokeLinejoin="round" d="M16.862 4.487l1.687-1.688a1.875 1.875 0 112.652 2.652L10.582 16.07a4.5 4.5 0 01-1.897 1.13L6 18l.8-2.685a4.5 4.5 0 011.13-1.897l8.932-8.931z" /></svg>
                  </button>
                  <button onClick={() => deleteLog(log.id)} className="p-1.5 rounded hover:bg-red-50" data-testid={`delete-trip-${log.id}`}>
                    <svg className="w-4 h-4 text-stone-400 hover:text-red-500" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="1.5"><path strokeLinecap="round" strokeLinejoin="round" d="M14.74 9l-.346 9m-4.788 0L9.26 9m9.968-3.21c.342.052.682.107 1.022.166m-1.022-.165L18.16 19.673a2.25 2.25 0 01-2.244 2.077H8.084a2.25 2.25 0 01-2.244-2.077L4.772 5.79m14.456 0a48.108 48.108 0 00-3.478-.397m-12 .562c.34-.059.68-.114 1.022-.165m0 0a48.11 48.11 0 013.478-.397m7.5 0v-.916c0-1.18-.91-2.164-2.09-2.201a51.964 51.964 0 00-3.32 0c-1.18.037-2.09 1.022-2.09 2.201v.916m7.5 0a48.667 48.667 0 00-7.5 0" /></svg>
                  </button>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      <Dialog open={showDialog} onOpenChange={setShowDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{editingLog ? "Edit Trip" : "Log Business Trip"}</DialogTitle>
            <DialogDescription>Record a business trip for your mileage deduction</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2"><Label>Date *</Label><Input type="date" value={form.trip_date} onChange={e => setForm({...form, trip_date: e.target.value})} data-testid="trip-date" /></div>
              <div className="space-y-2"><Label>Miles *</Label><Input type="number" step="0.1" placeholder="0.0" value={form.miles} onChange={e => setForm({...form, miles: e.target.value})} data-testid="trip-miles" /></div>
            </div>
            <div className="space-y-2"><Label>Purpose *</Label><Input placeholder="e.g., Client meeting, site visit" value={form.purpose} onChange={e => setForm({...form, purpose: e.target.value})} data-testid="trip-purpose" /></div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2"><Label>From</Label><Input placeholder="Start location" value={form.start_location} onChange={e => setForm({...form, start_location: e.target.value})} data-testid="trip-start" /></div>
              <div className="space-y-2"><Label>To</Label><Input placeholder="End location" value={form.end_location} onChange={e => setForm({...form, end_location: e.target.value})} data-testid="trip-end" /></div>
            </div>
            <div className="space-y-2"><Label>Parking & Tolls ($)</Label><Input type="number" step="0.01" placeholder="0.00" value={form.parking_tolls} onChange={e => setForm({...form, parking_tolls: e.target.value})} data-testid="trip-parking" /></div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowDialog(false)}>Cancel</Button>
            <Button onClick={saveLog} className="bg-[#1e3a5f] hover:bg-[#2d5a8e]" data-testid="save-trip-btn">{editingLog ? "Update" : "Log Trip"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default MileageTracker;
